<script setup lang="ts">
import type { Themeable } from '#theme'
import type { ColorSchemeMode } from '#imports'
import { useColorScheme, useDefaults, useI18n, useTheme, useTracking } from '#imports'

export interface UIColorSchemeSwitchProps extends Themeable {
    dataTest?: string
}

const _props = defineProps<UIColorSchemeSwitchProps>()
const props = useDefaults({
    props: _props,
    defaults: {
        color: 'secondary',
        dataTest: 'color-scheme-switch',
    },
})

const { t } = useI18n()
const { themeClass } = useTheme(() => ({ color: props.color }))
const { mode, setMode } = useColorScheme()
const { trackEvent } = useTracking()

// The chosen mode is the event name rather than a meta field, so adoption counts
// the same way the rest of the drawer's clicks do. Every event also carries the
// mode in effect when it fired, which is what tells adoption from switching.
function select(next: ColorSchemeMode) {
    if (next === mode.value) return

    trackEvent({
        event_name: `color-scheme_${next}_click`,
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })

    setMode(next)
}
</script>

<template>
    <div class="color-scheme-switch" :class="themeClass">
        <button
            type="button"
            class="color-scheme-switch__label"
            :class="{ 'color-scheme-switch__label--active': mode === 'light' }"
            :data-test="`${props.dataTest}-light`"
            @click="select('light')"
        >
            {{ t('core.components.UIColorSchemeSwitch.lightLabel') }}
        </button>

        <button
            type="button"
            role="switch"
            class="color-scheme-switch__track"
            :aria-checked="mode === 'dark'"
            :aria-label="t('core.components.UIColorSchemeSwitch.ariaLabel')"
            :data-test="props.dataTest"
            @click="select(mode === 'dark' ? 'light' : 'dark')"
        >
            <span class="color-scheme-switch__thumb">
                <UIIcon name="sun" size="14" class="color-scheme-switch__icon color-scheme-switch__icon--sun" />
                <UIIcon name="moon" size="14" class="color-scheme-switch__icon color-scheme-switch__icon--moon" />
            </span>
        </button>

        <button
            type="button"
            class="color-scheme-switch__label"
            :class="{ 'color-scheme-switch__label--active': mode === 'dark' }"
            :data-test="`${props.dataTest}-dark`"
            @click="select('dark')"
        >
            {{ t('core.components.UIColorSchemeSwitch.darkLabel') }}
        </button>
    </div>
</template>

<style lang="scss" scoped>
.color-scheme-switch {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;

    &__label {
        padding: 4px;
        border: none;
        background: none;
        cursor: pointer;
        font-family: inherit;
        font-size: $font-size-xs;
        font-weight: $text-bold;
        color: var(--on-surface, $body-font-color);
        opacity: 0.4;
        transition: opacity 0.15s ease-out;

        &--active {
            opacity: 1;
        }
    }

    &__track {
        position: relative;
        flex-shrink: 0;
        width: 52px;
        height: 30px;
        padding: 0;
        border: none;
        border-radius: var(--border-radius-rounded, 50px);
        background: var(--surface-soft, $toggle-slider-bg);
        cursor: pointer;
        transition: background 0.15s ease-out;

        &[aria-checked='true'] {
            background: var(--color-soft, var(--surface-soft, $toggle-slider-bg));
        }
    }

    &__thumb {
        position: absolute;
        top: 3px;
        left: 3px;
        display: grid;
        place-items: center;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        // Constant rather than a surface token: the thumb has to read as raised
        // against the track in both modes, and the sun/moon sit on top of it.
        background: #fff;
        box-shadow: 0 1px 3px rgb(0 0 0 / 28%);
        transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);

        [aria-checked='true'] & {
            transform: translateX(22px);
        }
    }

    &__icon {
        grid-area: 1 / 1;
        transition: opacity 0.15s ease-out;

        &--sun {
            color: $brand-warning;
            opacity: 1;
        }

        &--moon {
            color: var(--color, $blue);
            opacity: 0;
        }

        [aria-checked='true'] &--sun {
            opacity: 0;
        }

        [aria-checked='true'] &--moon {
            opacity: 1;
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .color-scheme-switch {
        &__track,
        &__thumb,
        &__icon {
            transition: none;
        }
    }
}
</style>
